import { useTranslation } from "@impero-com/common/utils/i18n";
import { Button } from "@impero-com/ui";
import { HeaderContext } from "@tanstack/react-table";
import { InputNumber, Popover, Select } from "antd";

import { ProgramOverview } from "api/generated/model";

import settingsIcon from "./overdue-column-settings.svg";
import * as theme from "./OverdueColumnHeader.css";
import { overdueColumnLabel, OverdueRange } from "./overdueColumns";

export interface OverdueColumnMeta {
    value: OverdueRange;
    onChange: (value: OverdueRange) => void;
}

export function OverdueColumnHeader({
    column,
}: HeaderContext<ProgramOverview, number>) {
    const { value, onChange } = column.columnDef.meta as OverdueColumnMeta;
    const { t } = useTranslation();
    const title = t("ControlHealth_OverdueColumn_Adjust");
    const under = value.comparison.type === "atMost";
    return (
        <div className={theme.header}>
            <span>{overdueColumnLabel(value, t)}</span>
            <span
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
            >
                <Popover
                    trigger="click"
                    placement="bottomRight"
                    overlayClassName={theme.popover}
                    title={title}
                    content={
                        <div className={theme.inputs}>
                            <span>
                                {t(
                                    under
                                        ? "ControlHealth_OverdueColumn_UnderLabel"
                                        : "ControlHealth_OverdueColumn_OverLabel"
                                )}
                            </span>
                            <InputNumber
                                aria-label={title}
                                min={under ? 1 : 0}
                                max={1_000_000}
                                precision={0}
                                value={value.comparison.value + (under ? 1 : 0)}
                                onChange={(number) => {
                                    if (number !== null) {
                                        onChange({
                                            ...value,
                                            comparison: {
                                                ...value.comparison,
                                                value: number - (under ? 1 : 0),
                                            },
                                        });
                                    }
                                }}
                                addonAfter={
                                    <Select
                                        aria-label={t(
                                            "ControlActivitiesList_TimeToDueDate_Unit"
                                        )}
                                        value={value.unit}
                                        onChange={(unit: "hours" | "days") =>
                                            onChange({ ...value, unit })
                                        }
                                        options={[
                                            {
                                                value: "hours",
                                                label: t(
                                                    "ControlActivitiesList_TimeToDueDate_HoursUnit"
                                                ),
                                            },
                                            {
                                                value: "days",
                                                label: t(
                                                    "ControlActivitiesList_TimeToDueDate_DaysUnit"
                                                ),
                                            },
                                        ]}
                                    />
                                }
                            />
                        </div>
                    }
                >
                    <Button
                        size="small"
                        icon={<img src={settingsIcon} alt="" />}
                        aria-label={`${title}: ${overdueColumnLabel(value, t)}`}
                    />
                </Popover>
            </span>
        </div>
    );
}
